/**
 * The site's client for the serving API: one GraphQL document per root field.
 *
 * Every page asks one of these operations and renders the item it returns.
 * The documents select every field, because an item is small and a page shows
 * most of it; the operation name is the root field's name, which is how the
 * local server in development answers the same requests without a GraphQL
 * library. The types are the serving item types themselves, imported from
 * `src/serve/items.ts`, so a field the site reads is a field the items carry.
 */
import type { BuildItem, OperationName } from '../../src/serve/api.js';
import type { CoalitionMapItem, FunderItem, GrantPage, OrganizationItem, SubjectFundersPage, SubjectItem } from '../../src/serve/items.js';
import { loadConfig } from './config.js';

const PARTY = 'id ein name listed project { id name }';
const EDGE = `id part funder { ${PARTY} } recipient { ${PARTY} } basis tier individual amount period purpose program confirmation { filedGrant amountAgrees } provenance { source filing objectId period page capturedAt }`;
const SUBJECT_REFERENCE = 'id code label';

const DOCUMENTS: Record<OperationName, string> = {
  organization: `query organization($ein: ID!) { organization(ein: $ein) { kind id ein name city state nteeCode subject { ${SUBJECT_REFERENCE} }
    masterFile { subsection foundationCode rulingDate status latestTaxPeriod assetAmount incomeAmount revenueAmount }
    filings { id objectId returnType taxPeriod taxPeriodEnd amended isCurrent source } mission { text filing taxPeriod } programs { text filing taxPeriod }
    grantsReceived { count total pages } grantsGiven { count total pages } sponsoredProjects { id name basis grants } funder coalitionMap sources } }`,
  grantsReceived: `query grantsReceived($ein: ID!, $page: Int) { grantsReceived(ein: $ein, page: $page) { kind list page pages edges { ${EDGE} } } }`,
  grantsGiven: `query grantsGiven($ein: ID!, $page: Int) { grantsGiven(ein: $ein, page: $page) { kind list page pages edges { ${EDGE} } } }`,
  funder: `query funder($ein: ID!) { funder(ein: $ein) { kind id ein name state periods { period count total } subjects { ${SUBJECT_REFERENCE} count total }
    states { state count total } recipients { listed unlisted names } peers { id ein name sharedRecipients } grantsGiven { count total pages } sources } }`,
  subject: `query subject($slug: ID!) { subject(slug: $slug) { kind id code label definition level broader { ${SUBJECT_REFERENCE} } narrower { ${SUBJECT_REFERENCE} }
    nteeCodes { code name } organizations funders { state count pages } } }`,
  subjectFunders: `query subjectFunders($slug: ID!, $state: String!, $page: Int) { subjectFunders(slug: $slug, state: $state, page: $page) { kind state page pages funders { id ein name years grants total } } }`,
  coalitionMap: `query coalitionMap($ein: ID!) { coalitionMap(ein: $ein) { kind id recipe version parameters window run anchor { ${PARTY} } funders { ${PARTY} } excluded { ${PARTY} } members { organization { ${PARTY} } score sharedFunders } } }`,
  coalitionMapByCluster: `query coalitionMapByCluster($window: String!, $hash: String!) { coalitionMapByCluster(window: $window, hash: $hash) { kind id recipe version parameters window run anchor { ${PARTY} } funders { ${PARTY} } excluded { ${PARTY} } members { organization { ${PARTY} } score sharedFunders } } }`,
  build: 'query build { build { kind goldRunId serveRunId counts } }',
};

interface GraphqlResponse<T> {
  readonly data?: Record<string, T | null>;
  readonly errors?: readonly { readonly message: string }[];
}

const request = async <T>(operation: OperationName, variables: Record<string, unknown>): Promise<T | null> => {
  const config = await loadConfig();
  const response = await fetch(config.graphqlUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-api-key': config.apiKey },
    body: JSON.stringify({ query: DOCUMENTS[operation], operationName: operation, variables }),
  });
  if (!response.ok) {
    throw new Error(`The API answered ${response.status}`);
  }
  const body = (await response.json()) as GraphqlResponse<T>;
  if (body.errors !== undefined && body.errors.length > 0) {
    throw new Error(body.errors.map((error) => error.message).join('; '));
  }
  const value = body.data?.[operation];
  if (operation === 'build' && value !== null && value !== undefined) {
    // AppSync returns the AWSJSON scalar as a string; the local server returns the object.
    const build = value as unknown as { counts: unknown };
    return { ...build, counts: typeof build.counts === 'string' ? (JSON.parse(build.counts) as unknown) : build.counts } as unknown as T;
  }
  return value ?? null;
};

export const api = {
  organization: (ein: string) => request<OrganizationItem>('organization', { ein }),
  grantsReceived: (ein: string, page: number) => request<GrantPage>('grantsReceived', { ein, page }),
  grantsGiven: (ein: string, page: number) => request<GrantPage>('grantsGiven', { ein, page }),
  funder: (ein: string) => request<FunderItem>('funder', { ein }),
  subject: (slug: string) => request<SubjectItem>('subject', { slug }),
  subjectFunders: (slug: string, state: string, page: number) => request<SubjectFundersPage>('subjectFunders', { slug, state, page }),
  coalitionMap: (ein: string) => request<CoalitionMapItem>('coalitionMap', { ein }),
  coalitionMapByCluster: (window: string, hash: string) => request<CoalitionMapItem>('coalitionMapByCluster', { window, hash }),
  build: () => request<BuildItem>('build', {}),
};
